import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import appIcon from '@/app-icon.svg';

/**
 * The authenticator QR code with the DTS logo in the middle. Uses the highest error correction (H, ~30% of the code
 * can be covered) and keeps the logo to about 22% of the width, so every scanner still reads it.
 */
export function MfaQrCode({ value, size = 216, label = 'QR code for your authenticator app' }: { value: string; size?: number; label?: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const el = canvas.current;
    if (!el) return;
    let cancelled = false;
    const scale = window.devicePixelRatio || 1;
    QRCode.toCanvas(el, value, { errorCorrectionLevel: 'H', margin: 2, width: size * scale, color: { dark: '#0B1426', light: '#FFFFFF' } })
      .then(() => {
        el.style.width = `${size}px`;
        el.style.height = `${size}px`;
        const img = new Image();
        img.onload = () => {
          if (cancelled) return;
          const g = el.getContext('2d');
          if (!g) return;
          const logo = Math.round(el.width * 0.22);
          const pad = Math.round(logo * 0.12);
          const x = Math.round((el.width - logo) / 2);
          // White plate behind the logo so its edge doesn't merge with nearby modules.
          g.fillStyle = '#FFFFFF';
          g.beginPath();
          g.roundRect(x - pad, x - pad, logo + pad * 2, logo + pad * 2, (logo + pad * 2) * 0.24);
          g.fill();
          g.drawImage(img, x, x, logo, logo);
        };
        img.src = appIcon;
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
    };
  }, [value, size]);

  if (failed) return <div className="flex items-center justify-center rounded-2xl bg-line text-[12px] text-muted" style={{ width: size, height: size }}>{label.includes('authenticator') ? "Couldn't draw the QR code. Use the key below." : "Couldn't draw the QR code."}</div>;
  return <canvas ref={canvas} width={size} height={size} className="rounded-2xl" aria-label={label} />;
}
